import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { updatePassword, rotateApiKey, getCreditsBalance } from '../api/client'
import Layout from '../components/Layout'
import LoadingButton from '../components/LoadingButton'
import ErrorMessage from '../components/ErrorMessage'
import CopyButton from '../components/CopyButton'

export default function Settings() {
  const { email, username, apiKey, creditsBalance, logout, updateCreditsBalance } = useAuthStore()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  const [showApiKey, setShowApiKey] = useState(false)
  const [rotateLoading, setRotateLoading] = useState(false)
  const [rotateError, setRotateError] = useState<string | null>(null)
  const [showRotateWarning, setShowRotateWarning] = useState(false)

  const [creditsLoading, setCreditsLoading] = useState(false)

  useEffect(() => {
    loadCreditsBalance()
  }, [])

  async function loadCreditsBalance() {
    setCreditsLoading(true)
    try {
      const response = await getCreditsBalance()
      updateCreditsBalance(response.balance)
    } catch (err) {
      // Silently fail - balance might not be available
    } finally {
      setCreditsLoading(false)
    }
  }

  async function handlePasswordUpdate(e: React.FormEvent) {
    e.preventDefault()
    setPasswordError(null)
    setPasswordSuccess(false)

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      return
    }

    setPasswordLoading(true)
    try {
      await updatePassword(currentPassword, newPassword)
      setPasswordSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setPasswordLoading(false)
    }
  }

  async function handleRotateApiKey() {
    setRotateError(null)
    setRotateLoading(true)
    try {
      await rotateApiKey()
      setShowRotateWarning(false)
      // Note: The new API key would be returned and should be saved
      // For now, recommend user to re-login
      setRotateError('API key rotated. Please log out and back in to use the new key.')
    } catch (err) {
      setRotateError(err instanceof Error ? err.message : 'Failed to rotate API key')
    } finally {
      setRotateLoading(false)
    }
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Settings</h1>

        {/* Account Information */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Account</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Email</label>
              <p className="text-gray-900">{email || 'Not set'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Username</label>
              <p className="text-gray-900">{username || 'Not set'}</p>
            </div>
          </div>
        </section>

        {/* Credits Balance */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Credits</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-gray-900">
                {creditsLoading ? (
                  <span className="text-gray-400">Loading...</span>
                ) : creditsBalance !== null ? (
                  creditsBalance.toLocaleString()
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </p>
              <p className="text-sm text-gray-500">Available credits</p>
            </div>
            <LoadingButton
              variant="secondary"
              size="sm"
              onClick={loadCreditsBalance}
              loading={creditsLoading}
            >
              Refresh
            </LoadingButton>
          </div>
        </section>

        {/* Change Password */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h2>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {passwordError && <ErrorMessage message={passwordError} onDismiss={() => setPasswordError(null)} />}
            {passwordSuccess && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                Password updated successfully!
              </div>
            )}

            <LoadingButton
              type="submit"
              loading={passwordLoading}
              variant="primary"
              disabled={!currentPassword || !newPassword || !confirmPassword}
            >
              Update Password
            </LoadingButton>
          </form>
        </section>

        {/* API Key */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">API Key</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Your API Key</label>
              <div className="flex items-center gap-2">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey || ''}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  {showApiKey ? 'Hide' : 'Show'}
                </button>
                {apiKey && <CopyButton text={apiKey} size="md" />}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              {!showRotateWarning ? (
                <button
                  type="button"
                  onClick={() => setShowRotateWarning(true)}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Rotate API Key
                </button>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 mb-3">
                    <strong>Warning:</strong> Rotating your API key will invalidate the current key immediately.
                    Any integrations using this key will stop working until updated.
                  </p>
                  <p className="text-sm text-red-700 mb-3">
                    Check the{' '}
                    <Link to="/skill" className="underline">
                      API documentation (SKILL.md)
                    </Link>{' '}
                    for integration instructions.
                  </p>
                  {rotateError && <ErrorMessage message={rotateError} className="mb-3" />}
                  <div className="flex gap-3">
                    <LoadingButton
                      variant="danger"
                      size="sm"
                      onClick={handleRotateApiKey}
                      loading={rotateLoading}
                    >
                      Confirm Rotation
                    </LoadingButton>
                    <button
                      type="button"
                      onClick={() => setShowRotateWarning(false)}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Logout */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Session</h2>
          <LoadingButton
            variant="secondary"
            onClick={logout}
          >
            Log Out
          </LoadingButton>
        </section>
      </div>
    </Layout>
  )
}
